import React, { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom';
import URL2 from './config.js'
import MainTemplate from './components/MainTemplate/MainTemplate.js';
import Heading from './components/Heading/Heading.js';
import checkStatus, { readStatusWithRetries } from './jsFunctions/checkStatus.js'
import { getCampusPaymentCharges, getPaymentResumeState, markCampusChargeFailed } from './utils/campusPaymentCharges.js'
import { cardPaymentSteps, formatMoney } from './utils/campusPaymentSteps.js'
import Typography from '@mui/material/Typography';
import { shouldRedirectToCompletion } from './utils/campusPaymentStatus.js'
import { formatPaymentRejectionDetail } from './utils/paymentRejectionDetail.js'
import { Box, Button } from '@mui/material';

const PaymentResponse = () => {
    const navigate = useNavigate();
    const [searchParams, setsearchParams] = useSearchParams();
    const SessionID = searchParams.get('SessionId');
    const ParentID = searchParams.get('ParentID');
    const college = searchParams.get('college');
    const famName = searchParams.get('famName');
    const TotalResAmount = searchParams.get('TotalResAmount');
    const TotalDepFees = searchParams.get('TotalDepFees');
    const TotalSchoolFeeswodisc = searchParams.get('TotalSchoolFeeswodisc');
    const TotalDiscount = searchParams.get('TotalDiscount');
    const TotalFeesWithDiscount = searchParams.get('TotalFeesWithDiscount');
    const PayableAmount = searchParams.get('PayableAmount');
    const CustomAmount = searchParams.get('CustomAmount');
    const RemainingAmount = searchParams.get('RemainingAmount');
    const AmountPaidFromAICCredit = searchParams.get('AmountPaidFromAICCredit');
    const AmountPaidFromBpoint = searchParams.get('AmountPaidFromBpoint');
    const ResultKey = searchParams.get('ResultKey');

    const [ErrorTransactionMsg, SetErrorTransactionMsg] = useState('');
    const [Status, SetStatus] = useState();
    const [showRetry, setShowRetry] = useState(false);
    const hasProcessed = useRef(false); // Prevent duplicate processing

    const setPaymentRejectedStatus = async (TransactionResultresponse) => {
        try {
            const txnResp = TransactionResultresponse?.TxnResp || {};
            const apiResponse = TransactionResultresponse?.APIResponse || {};
            let TransactionDateTime = apiResponse.ResponseCode === 0 ? txnResp.ProcessedDateTime : new Date();
            const dateTime = new Date(TransactionDateTime);

            const year = dateTime.getFullYear();
            const month = dateTime.getMonth() + 1;
            const day = dateTime.getDate();

            const hours = dateTime.getHours();
            const minutes = dateTime.getMinutes();
            const seconds = dateTime.getSeconds();
            let datePart = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
            let timePart = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

            const commonData = {
                SessionID: SessionID,
                parentID: ParentID,
                college: college,
                famName: famName,
                TotalResAmount: TotalResAmount,
                TotalDepFees: TotalDepFees,
                TotalSchoolFeeswodisc: TotalSchoolFeeswodisc,
                TotalDiscount: TotalDiscount,
                TotalFeesWithDiscount: TotalFeesWithDiscount,
                PayableAmount: PayableAmount,
                CustomAmount: CustomAmount,
                RemainingAmount: RemainingAmount,
                AmountPaidFromBpoint: AmountPaidFromBpoint,
                AmountPaidFromAICCredit: AmountPaidFromAICCredit,
                BillerCode: 0,
                ReceiptNumber: txnResp.ReceiptNumber ? txnResp.ReceiptNumber : 0,
                MerchantNumber: txnResp.MerchantNumber ? txnResp.MerchantNumber : 0,
                TransactionResponse: apiResponse.ResponseCode !== 0 ? (apiResponse.ResponseText || 'API Error') : txnResp.ResponseText,
                datePart: datePart,
                timePart: timePart
            }

            await Promise.all([
                URL2.post('/setPaymentRejectedStatus', {
                    ...commonData,
                    renrol_status: 'Rejected',
                }),
                URL2.post('/parent/parent-pay-detail-logs', commonData)
            ]);

        } catch (error) {
            console.error('Error in setPaymentRejectedStatus:', error);
        }
    }
    const SetRejection = (TransactionResultresponse) => {
        const detail = formatPaymentRejectionDetail(TransactionResultresponse);
        SetErrorTransactionMsg(
            `${detail ? `${detail}. ` : ''}We regret to inform you that the payment transaction has encountered an issue. Kindly attempt the transaction again later. If the problem persists, please email helpdesk@aic.wa.edu.au or contact the office at 0893622100 extension 298 for further assistance.`
        );
        if (TransactionResultresponse) {
            setPaymentRejectedStatus(TransactionResultresponse);
        }
        markCampusChargeFailed(SessionID);
        setShowRetry(true);
    }
    useEffect(() => {
        // Prevent duplicate execution
        if (hasProcessed.current) return;

        const processPaymentResponse = async () => {
            try {
                if (localStorage.getItem("LoginToken") === null) {
                    navigate('/');
                    return;
                }

                // Asked a few times before giving up: a status that cannot be read is not a reason
                // to stop here. It only decides whether everything was already paid; the card result
                // is read either way. This page used to do nothing at all when the status came back
                // empty (a failed request, a slow AIC Connect, an expired token), and a parent who had
                // just paid sat on "Confirming payment..." until they refreshed.
                const data = await readStatusWithRetries(checkStatus);
                const statusKnown = Boolean(data && data.RenrolStatus !== undefined);
                if (statusKnown) SetStatus(data.RenrolStatus);

                if (statusKnown && shouldRedirectToCompletion(data)) {
                    navigate('/reenrolmentcompleted', {
                        state: {
                            t_date: data.t_date,
                            t_time: data.t_time,
                            campusStatuses: data.campusStatuses || [],
                        }
                    });
                } else {
                    if (!ResultKey) {
                        SetErrorTransactionMsg('Payment did not complete. No transaction result was returned by BPoint. Please try the payment again.');
                        markCampusChargeFailed(SessionID);
                        setShowRetry(true);
                        hasProcessed.current = true;
                        return;
                    }

                    const response = await URL2.post('/getTransactionResult', {
                        ResultKey: ResultKey,
                        college: college
                    });

                    const TransactionResultresponse = response.data.response;
                    const TransactionAPIResponse = TransactionResultresponse?.APIResponse;
                    const TransactionBankResponse = TransactionResultresponse?.TxnResp;

                    if (TransactionAPIResponse?.ResponseCode === 0 && TransactionBankResponse?.BankResponseCode === "00") {
                        navigate('/thankyou', {
                            state: {
                                SessionID: SessionID,
                                parentID: ParentID,
                                college: college,
                                famName: famName,
                                TotalResAmount: TotalResAmount,
                                TotalDepFees: TotalDepFees,
                                TotalSchoolFeeswodisc: TotalSchoolFeeswodisc,
                                TotalDiscount: TotalDiscount,
                                TotalFeesWithDiscount: TotalFeesWithDiscount,
                                PayableAmount: PayableAmount,
                                CustomAmount: CustomAmount,
                                RemainingAmount: RemainingAmount,
                                AmountPaidFromBpoint: AmountPaidFromBpoint,
                                AmountPaidFromAICCredit: AmountPaidFromAICCredit,
                                BillerCode: 0,
                                ReceiptNumber: TransactionResultresponse.TxnResp.ReceiptNumber,
                                TransactionDateTime: TransactionResultresponse.TxnResp.ProcessedDateTime,
                                MerchantNumber: TransactionResultresponse.TxnResp.MerchantNumber,
                                TransactionResponse: TransactionBankResponse.ResponseText,
                            }
                        });
                    } else {
                        SetRejection(TransactionResultresponse);
                    }
                }

                hasProcessed.current = true;

            } catch (error) {
                console.error("An error occurred:", error);
                const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
                SetErrorTransactionMsg(apiMessage || 'Unable to confirm the payment result. Please try again, or contact the office if you were charged.');
                hasProcessed.current = true;
            }
        };

        processPaymentResponse();
    }, []); // Empty dependency array

    // When this was the second payment, the first is paid and stays paid: say so, before anything
    // else, so a family does not think everything was lost or pay the first campus twice.
    const paidSoFar = showRetry
        ? cardPaymentSteps(getCampusPaymentCharges()).filter((step) => step.paid && String(step.SessionID) !== String(SessionID))
        : [];

    const content = <>
        <Heading text={`${ErrorTransactionMsg || 'Confirming payment...'}`} />
        {paidSoFar.length > 0 && (
            <Box sx={{ maxWidth: 640, mx: 'auto', mt: 2, p: 2, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '12px' }} data-testid="paid-campuses-safe">
                <Typography sx={{ color: '#065f46', fontWeight: 700, fontSize: '15px' }}>
                    {`Your payment for ${paidSoFar.map((step) => `${step.college} (${formatMoney(step.amount)})`).join(' and ')} went through and is safe.`}
                </Typography>
                <Typography sx={{ color: '#065f46', fontSize: '14px', mt: 0.5 }}>
                    {`Only the payment for ${college || 'this campus'} did not go through. You do not need to pay ${paidSoFar.length === 1 ? paidSoFar[0].college : 'those campuses'} again.`}
                </Typography>
            </Box>
        )}
        {showRetry && (
            <Button
                variant="contained"
                sx={{ mt: 2, display: 'block', mx: 'auto' }}
                onClick={() => {
                    const resume = getPaymentResumeState();
                    navigate('/bpointpaymentform', {
                        replace: true,
                        state: resume,
                    });
                }}
            >
                {college ? `Try the ${college} payment again` : 'Try payment again'}
            </Button>
        )}
    </>

    return (
        <MainTemplate content={content} SessionID={SessionID} college={college} />
    )
}

export default PaymentResponse